<script setup lang="ts">
  import { computed } from 'vue';
  import type { FlipDirection, FlipEdge, FlipLayout } from './flipView';

  interface FlipViewProps {
    direction?: FlipDirection;
    edge?: FlipEdge;
    layout?: FlipLayout;
    overlay?: boolean;
  }

  const props = withDefaults(defineProps<FlipViewProps>(), {
    direction: 'forward',
    edge: 'right',
    layout: 'flow',
    overlay: false,
  });

  interface FlipViewEmits {
    (e: 'after-enter'): void;
  }

  const emit = defineEmits<FlipViewEmits>();

  const MOVING_LAYER_CLASS = 'flip-view__layer flip-view__layer_moving';
  const STATIC_LAYER_CLASS = 'flip-view__layer flip-view__layer_static';

  const transitionName = computed<string>(() => `flip-view-${props.direction}-${props.edge}`);
  const isForward = computed<boolean>(() => props.direction === 'forward');
  const enterActiveClass = computed<string>(() => (isForward.value ? MOVING_LAYER_CLASS : STATIC_LAYER_CLASS));
  const leaveActiveClass = computed<string>(() => (isForward.value ? STATIC_LAYER_CLASS : MOVING_LAYER_CLASS));
</script>

<template>
  <div
    :class="['flip-view', `flip-view_${layout}`, { 'flip-view_overlay': overlay }]"
    data-testid="flip-view"
  >
    <transition
      :name="transitionName"
      :enter-active-class="enterActiveClass"
      :leave-active-class="leaveActiveClass"
      @after-enter="emit('after-enter')"
    >
      <slot>
        <span class="flip-view__blank" />
      </slot>
    </transition>
  </div>
</template>

<style lang="scss">
  .flip-view {
    box-sizing: border-box;
    position: relative;
    width: 100%;
  }

  .flip-view_flow {
    overflow-x: clip;
  }

  .flip-view_stacked {
    height: 100%;
    overflow: hidden;
  }

  .flip-view_stacked > * {
    inset: 0;
    position: absolute;
  }

  .flip-view__blank {
    display: block;
  }

  .flip-view .flip-view__layer.flip-view__layer_moving {
    box-shadow: var(--flip-view-shadow, none);
    transition: transform var(--flip-view-duration, 0.3s) ease;
    will-change: transform;
    z-index: var(--flip-view-layer, 1001);
  }

  .flip-view_overlay .flip-view__layer.flip-view__layer_moving {
    inset: 0;
    overflow: hidden;
    position: fixed;
  }

  .flip-view .flip-view__layer.flip-view__layer_static {
    pointer-events: none;
    transition: opacity var(--flip-view-duration, 0.3s) linear;
  }

  .flip-view-forward-right-enter,
  .flip-view-back-right-leave-to {
    transform: translateX(100%);
  }

  .flip-view-forward-left-enter,
  .flip-view-back-left-leave-to {
    transform: translateX(-100%);
  }

  @media (prefers-reduced-motion: reduce) {
    .flip-view .flip-view__layer.flip-view__layer_moving,
    .flip-view .flip-view__layer.flip-view__layer_static {
      transition-duration: 0.01ms;
    }
  }
</style>
